/**
 * JobCategoriesStep — Step 1 of the Job Management Wizard
 *
 * Table of job categories with add/edit dialog, tooltips, and task type assignment.
 */

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { JobCategoryItem } from '@skewb-works/contracts';
import {
  ArrowDown,
  ArrowUp,
  ClipboardList,
  HelpCircle,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useAssignTask,
  useCategoryTasks,
  useCreateJobCategory,
  useDeleteJobCategory,
  useJobCategories,
  useRemoveTaskAssignment,
  useReorderTasks,
  useTaskTypes,
  useUpdateJobCategory,
} from '../hooks/useJobManagement';

function Tooltip({ text }: { text: string }) {
  return (
    <span className="relative group inline-flex ml-1 cursor-help">
      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-xs text-white bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 rounded-md shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
        {text}
      </span>
    </span>
  );
}

interface FormState {
  name: string;
  code: string;
  type: string;
  time: string;
}

const emptyForm: FormState = { name: '', code: '', type: '', time: '' };

export default function JobCategoriesStep() {
  const { data, isLoading } = useJobCategories();
  const { data: taskTypesData } = useTaskTypes();
  const createMutation = useCreateJobCategory();
  const updateMutation = useUpdateJobCategory();
  const deleteMutation = useDeleteJobCategory();
  const assignMutation = useAssignTask();
  const removeMutation = useRemoveTaskAssignment();
  const reorderMutation = useReorderTasks();

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [taskSearch, setTaskSearch] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const categories = data?.data ?? [];
  const allTaskTypes = taskTypesData?.data ?? [];
  const { data: categoryTasksData } = useCategoryTasks(editingId);
  const assignedTasks = categoryTasksData?.data ?? [];

  const availableTaskTypes = useMemo(() => {
    const assignedIds = new Set(assignedTasks.map((t) => t.taskTypeId));
    let pool = allTaskTypes.filter((tt) => !assignedIds.has(tt.id));
    if (taskSearch) {
      const q = taskSearch.toLowerCase();
      pool = pool.filter((tt) => tt.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allTaskTypes, assignedTasks, taskSearch]);

  const filtered = useMemo(() => {
    if (!search) return categories;
    const q = search.toLowerCase();
    return categories.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.code?.toLowerCase().includes(q) ||
        c.type?.toLowerCase().includes(q),
    );
  }, [categories, search]);

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setTaskSearch('');
    setDialogOpen(true);
  }

  function openEdit(cat: JobCategoryItem) {
    setEditingId(cat.id);
    setForm({
      name: cat.name ?? '',
      code: cat.code ?? '',
      type: cat.type ?? '',
      time: cat.time != null ? String(cat.time) : '',
    });
    setTaskSearch('');
    setDialogOpen(true);
  }

  async function handleSave() {
    const body = {
      name: form.name,
      code: form.code || undefined,
      type: form.type || undefined,
      time: form.time ? Number(form.time) : undefined,
    };

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, body });
    } else {
      const result = await createMutation.mutateAsync(body);
      setEditingId(result.id);
    }
    if (!editingId) {
      // Keep dialog open for new categories so user can assign tasks
    } else {
      setDialogOpen(false);
    }
  }

  async function handleSaveAndClose() {
    if (!editingId) {
      await handleSave();
    }
    setDialogOpen(false);
  }

  async function handleAssignTask(taskTypeId: number) {
    if (!editingId) return;
    await assignMutation.mutateAsync({
      jobCategoryId: editingId,
      taskTypeId,
      orderNumber: String(assignedTasks.length + 1),
    });
  }

  async function handleRemoveTask(assignmentId: number) {
    if (!editingId) return;
    await removeMutation.mutateAsync({ id: assignmentId, categoryId: editingId });
  }

  async function handleMoveTask(index: number, direction: -1 | 1) {
    if (!editingId) return;
    const list = [...assignedTasks];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    await reorderMutation.mutateAsync({
      categoryId: editingId,
      taskIds: list.map((t) => t.id),
    });
  }

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={openCreate} style={{ backgroundColor: '#E84A2B' }} className="text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>
                <span className="flex items-center">
                  Code
                  <Tooltip text="Used to match inbound work orders from Ignite Core (orderType field)" />
                </span>
              </TableHead>
              <TableHead>
                <span className="flex items-center">
                  Type
                  <Tooltip text="Classification group for this category (e.g. Standard, Emergency)" />
                </span>
              </TableHead>
              <TableHead className="text-center">Tasks</TableHead>
              <TableHead className="text-center">System</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {search ? 'No categories match your search' : 'No job categories found'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell className="font-medium">{cat.name ?? '-'}</TableCell>
                  <TableCell>
                    {cat.code ? (
                      <Badge variant="outline" className="text-xs font-mono">
                        {cat.code}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>{cat.type ?? '-'}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">{cat.taskCount}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {cat.isSystem ? (
                      <Badge variant="secondary" className="text-xs">
                        System
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(cat)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteConfirmId(cat.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Job Category' : 'Add Job Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name *</Label>
              <Input
                id="cat-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Enter category name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cat-code" className="flex items-center">
                  Code
                  <Tooltip text="Matched against the orderType field in inbound work orders from Ignite Core" />
                </Label>
                <Input
                  id="cat-code"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="e.g. ISOXXXX"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-type" className="flex items-center">
                  Type
                  <Tooltip text="Classification group — used to organise categories (e.g. Standard, Emergency)" />
                </Label>
                <Input
                  id="cat-type"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  placeholder="e.g. Standard"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-time" className="flex items-center">
                Duration (hours)
                <Tooltip text="Default estimated duration for jobs in this category, used when scheduling" />
              </Label>
              <Input
                id="cat-time"
                type="number"
                value={form.time}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                placeholder="e.g. 4"
                min={0}
              />
            </div>

            {/* Task Types — only show when editing (need a saved category ID) */}
            {editingId && (
              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="flex items-center">
                  Assigned Task Types
                  <Tooltip text="Task types that belong to this category — engineers see these tasks when working on a job of this type" />
                </Label>

                {/* Ordered list */}
                {assignedTasks.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {assignedTasks.map((task, idx) => (
                      <div key={task.id} className="flex items-center gap-2 px-3 py-2">
                        <span className="text-xs font-medium text-muted-foreground w-5">
                          {idx + 1}.
                        </span>
                        <ClipboardList className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="flex-1 text-sm text-foreground">
                          {task.taskTypeName ?? `Task #${task.taskTypeId}`}
                        </span>
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => handleMoveTask(idx, -1)}
                            disabled={idx === 0}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveTask(idx, 1)}
                            disabled={idx === assignedTasks.length - 1}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-destructive"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {assignedTasks.length === 0 && (
                  <p className="text-xs text-muted-foreground">No task types assigned yet.</p>
                )}

                {/* Add task type */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-2 border-b bg-muted/30">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search and add task types..."
                        value={taskSearch}
                        onChange={(e) => setTaskSearch(e.target.value)}
                        className="pl-8 h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div className="max-h-[150px] overflow-y-auto">
                    {availableTaskTypes.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        {assignedTasks.length > 0 && !taskSearch
                          ? 'All task types have been added'
                          : taskSearch
                            ? 'No task types match your search'
                            : 'No task types available'}
                      </div>
                    ) : (
                      availableTaskTypes.map((tt) => (
                        <button
                          key={tt.id}
                          type="button"
                          onClick={() => handleAssignTask(tt.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm"
                        >
                          <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-foreground">{tt.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            {!editingId ? (
              <Button
                onClick={handleSave}
                disabled={!form.name.trim() || isSaving}
                style={{ backgroundColor: '#E84A2B' }}
                className="text-white"
              >
                {isSaving ? 'Saving...' : 'Create'}
              </Button>
            ) : (
              <Button
                onClick={handleSaveAndClose}
                disabled={!form.name.trim() || isSaving}
                style={{ backgroundColor: '#E84A2B' }}
                className="text-white"
              >
                {isSaving ? 'Saving...' : 'Save & Close'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job Category</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this job category? This action can be reversed by an
            administrator.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
